<template>
  <div class="ma-page py-3">
    <header class="ma-toolbar">
      <div class="ma-toolbar__title">
        <span class="ma-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>My Attendance</h1>
          <p>Your own clock-in, clock-out and hours on-site, from the office biometric device. First punch of the day = clock-in, last = clock-out.</p>
        </div>
      </div>
      <div class="ma-toolbar__actions">
        <button type="button" class="ma-refresh" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadReport">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ma-refresh__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="ma-alert">{{ errorMessage }}</CAlert>

    <CAlert v-if="!isLoading && mapped === false" color="warning" show class="ma-alert">
      You haven't been linked to a biometric PIN yet, so your punches can't be shown. Please ask an administrator to map your fingerprint on your user profile.
    </CAlert>

    <!-- Today at a glance -->
    <section class="ma-today" :class="'ma-today--' + todayTone">
      <div class="ma-today__label">
        <span class="ma-today__dot" aria-hidden="true"></span>
        <div>
          <span class="ma-today__title">Today</span>
          <span class="ma-today__date">{{ todayLongDisplay }}</span>
        </div>
      </div>
      <div class="ma-today__stats">
        <div class="ma-today__stat">
          <span class="ma-today__stat-label">Check In</span>
          <span class="ma-today__stat-value">{{ todayRow ? todayRow.firstInDisplay : '—' }}</span>
        </div>
        <div class="ma-today__stat">
          <span class="ma-today__stat-label">Check Out</span>
          <span class="ma-today__stat-value">{{ todayRow && todayRow.punches > 1 ? todayRow.lastOutDisplay : '—' }}</span>
        </div>
        <div class="ma-today__stat">
          <span class="ma-today__stat-label">Hours</span>
          <span class="ma-today__stat-value">{{ todayRow ? todayRow.hoursDisplay : '0h' }}</span>
        </div>
        <div class="ma-today__stat">
          <span class="ma-today__stat-label">Status</span>
          <span :class="['ma-pill', 'ma-pill--' + todayStatus]">
            <span class="ma-pill__dot" aria-hidden="true"></span>{{ statusLabel(todayStatus) }}
          </span>
        </div>
      </div>
    </section>

    <section class="ma-section">
      <header class="ma-section__head">
        <h2>Date Range</h2>
        <small class="ma-section__hint">{{ rangeLabel }}</small>
      </header>
      <div class="ma-section__body">
        <DateRangePicker v-model="dateRange" />
      </div>
    </section>

    <div class="ma-kpis">
      <div v-for="kpi in kpis" :key="kpi.key" class="ma-kpi">
        <span class="ma-kpi__icon" :class="'ma-kpi__icon--' + kpi.tone" aria-hidden="true" v-html="kpi.icon"></span>
        <div class="ma-kpi__meta">
          <span class="ma-kpi__label">{{ kpi.label }}</span>
          <span class="ma-kpi__value">{{ kpi.value }}<small v-if="kpi.unit" class="ma-kpi__unit">{{ kpi.unit }}</small></span>
        </div>
      </div>
    </div>

    <BaseTable
      title="Daily Hours"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :paginated="tableRows.length > pageSize"
      :page-size="pageSize"
      :default-sort="{ column: 'date', direction: 'desc' }"
      empty-text="No attendance recorded for the selected dates."
    >
      <template #cell-date="{ row }">
        <div class="ma-date">
          <span class="ma-date__day">{{ row.dateDisplay }}</span>
          <span class="ma-date__weekday">{{ row.weekday }}</span>
        </div>
      </template>

      <template #cell-first_in="{ row }">
        <span v-if="row.first_in" class="ma-time">{{ row.firstInDisplay }}</span>
        <span v-else class="ma-muted">—</span>
      </template>

      <template #cell-last_out="{ row }">
        <span v-if="row.punches > 1" class="ma-time">{{ row.lastOutDisplay }}</span>
        <span v-else class="ma-muted">—</span>
      </template>

      <template #cell-hours="{ row }">
        <div v-if="row.punches" class="ma-hours">
          <div class="ma-hours__head"><strong>{{ row.hoursDisplay }}</strong></div>
          <div class="ma-bar" :title="row.hours + ' h'">
            <span class="ma-bar__fill" :class="'ma-bar__fill--' + row.barTone" :style="{ width: row.barPct + '%' }"></span>
          </div>
        </div>
        <span v-else class="ma-muted">—</span>
      </template>

      <template #cell-punches="{ row }">
        <span class="ma-punches">{{ row.punches }}</span>
      </template>

      <template #cell-status="{ row }">
        <span :class="['ma-pill', 'ma-pill--' + row.status]">
          <span class="ma-pill__dot" aria-hidden="true"></span>{{ statusLabel(row.status) }}
        </span>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'

// A nominal full workday — the hours bar fills relative to this, capped at 100%.
const STANDARD_WORKDAY_HOURS = 9

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default {
  name: 'MyAttendance',
  components: { DateRangePicker, BaseTable },
  data () {
    const today = this.localToday()
    return {
      fromDate: today.slice(0, 8) + '01',
      toDate: today,
      rows: [],
      mapped: null,
      isLoading: false,
      errorMessage: '',
      pageSize: 31,
      columns: [
        { key: 'date', label: 'Date' },
        { key: 'first_in', label: 'First In' },
        { key: 'last_out', label: 'Last Out' },
        { key: 'hours', label: 'Hours', align: 'right', width: '150px' },
        { key: 'punches', label: 'Punches', align: 'right' },
        { key: 'status', label: 'Status' }
      ]
    }
  },
  computed: {
    dateRange: {
      get () {
        return { fromDate: this.fromDate, toDate: this.toDate }
      },
      set (value) {
        this.fromDate = (value && value.fromDate) || this.fromDate
        this.toDate = (value && value.toDate) || this.toDate
        this.loadReport()
      }
    },
    rangeLabel () {
      if (this.fromDate === this.toDate) return this.display(this.fromDate)
      return `${this.display(this.fromDate)} → ${this.display(this.toDate)}`
    },
    tableRows () {
      const maxHours = Math.max(STANDARD_WORKDAY_HOURS, ...this.rows.map(r => Number(r.hours) || 0))
      return this.rows.map((r) => {
        const hours = Number(r.hours) || 0
        return {
          ...r,
          dateDisplay: this.display(r.date),
          weekday: this.weekday(r.date),
          firstInDisplay: this.timeDisplay(r.first_in),
          lastOutDisplay: this.timeDisplay(r.last_out),
          hoursDisplay: this.hoursDisplay(hours),
          barPct: Math.min(100, Math.round((hours / maxHours) * 100)),
          barTone: this.barTone(hours)
        }
      })
    },
    todayRow () {
      return this.tableRows.find(r => r.date === this.localToday()) || null
    },
    todayStatus () {
      return this.todayRow ? this.todayRow.status : 'absent'
    },
    todayTone () {
      return this.todayStatus === 'present' ? 'present' : this.todayStatus === 'incomplete' ? 'incomplete' : 'absent'
    },
    todayLongDisplay () {
      const iso = this.localToday()
      const [y, m, d] = iso.split('-')
      return `${this.weekday(iso)}, ${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`
    },
    presentDays () {
      return this.rows.filter(r => r.punches > 0).length
    },
    totalHours () {
      return this.rows.reduce((sum, r) => sum + (Number(r.hours) || 0), 0)
    },
    avgHours () {
      return this.presentDays ? this.totalHours / this.presentDays : 0
    },
    kpis () {
      return [
        {
          key: 'days',
          label: 'Days Present',
          value: this.presentDays,
          tone: 'present',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>'
        },
        {
          key: 'total',
          label: 'Total Hours',
          value: this.formatHoursValue(this.totalHours),
          unit: 'h',
          tone: 'brand',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>'
        },
        {
          key: 'avg',
          label: 'Avg / Present Day',
          value: this.formatHoursValue(this.avgHours),
          unit: 'h',
          tone: 'brand',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>'
        }
      ]
    }
  },
  created () {
    this.loadReport()
  },
  methods: {
    localToday () {
      const d = new Date()
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      return local.toISOString().slice(0, 10)
    },
    display (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      return `${d}-${m}-${y}`
    },
    weekday (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-').map(Number)
      const date = new Date(y, m - 1, d)
      return WEEKDAYS[date.getDay()]
    },
    timeDisplay (t) {
      if (!t) return '—'
      const [hStr, mStr] = String(t).split(':')
      let h = Number(hStr)
      const m = mStr || '00'
      if (!Number.isFinite(h)) return t
      const suffix = h >= 12 ? 'PM' : 'AM'
      h = h % 12 || 12
      return `${h}:${m} ${suffix}`
    },
    hoursDisplay (hours) {
      const h = Math.floor(hours)
      const m = Math.round((hours - h) * 60)
      if (m === 0) return `${h}h`
      return `${h}h ${String(m).padStart(2, '0')}m`
    },
    formatHoursValue (hours) {
      return (Math.round(hours * 10) / 10).toFixed(1)
    },
    barTone (hours) {
      if (hours >= 8) return 'full'
      if (hours >= 4) return 'mid'
      return 'low'
    },
    statusLabel (s) {
      return { present: 'Present', absent: 'Not In Yet', incomplete: 'One Punch' }[s] || s
    },
    loadReport () {
      this.errorMessage = ''
      this.isLoading = true
      axios.get(`${this.$apiAdress}/api/attendance/my-hours`, {
        params: { from: this.fromDate, to: this.toDate }
      })
        .then(r => {
          this.rows = (r.data && r.data.rows) || []
          this.mapped = r.data ? r.data.mapped !== false : null
        })
        .catch(e => { this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Could not load your attendance.' })
        .finally(() => { this.isLoading = false })
    }
  }
}
</script>

<style scoped>
.ma-page { padding: 0.25rem 0; }

/* ── Toolbar ── */
.ma-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; }
.ma-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.ma-toolbar__accent { width: 4px; height: 36px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }
.ma-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.ma-toolbar__title p { margin: 0.12rem 0 0; font-size: 0.78rem; color: #6b7280; max-width: 640px; }
.ma-toolbar__actions { display: flex; align-items: center; gap: 0.5rem; }

.ma-refresh { display: inline-flex; align-items: center; gap: 0.45rem; height: 34px; padding: 0 0.85rem; border-radius: 8px; font-size: 0.82rem; font-weight: 600; background: #fff; border: 1px solid #e5e9f2; color: #1f2a44; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04); cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease; }
.ma-refresh:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12); }
.ma-refresh:disabled { cursor: progress; background: #f8fafc; color: #4b5563; }
.ma-refresh__icon { flex-shrink: 0; color: #16a07a; transition: transform 0.4s ease; }
.ma-refresh:hover:not(:disabled) .ma-refresh__icon { transform: rotate(60deg); }
.ma-refresh.is-loading .ma-refresh__icon { animation: ma-spin 0.9s linear infinite; }
@keyframes ma-spin { to { transform: rotate(360deg); } }
.ma-refresh:focus { outline: none; }
.ma-refresh::-moz-focus-inner { border: 0; }
.ma-refresh:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.ma-alert { margin: 0 0 1rem; border-radius: 10px; font-size: 0.85rem; }

/* ── Today card ── */
.ma-today { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; background: #fff; border: 1px solid #e5e9f2; border-left-width: 4px; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); padding: 0.95rem 1.15rem; margin-bottom: 1.25rem; }
.ma-today--present { border-left-color: #16a07a; }
.ma-today--incomplete { border-left-color: #f59e0b; }
.ma-today--absent { border-left-color: #cbd5e1; }
.ma-today__label { display: flex; align-items: center; gap: 0.6rem; }
.ma-today__dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.ma-today--present .ma-today__dot { background: #16a07a; box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.16); }
.ma-today--incomplete .ma-today__dot { background: #f59e0b; box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.16); }
.ma-today--absent .ma-today__dot { background: #cbd5e1; box-shadow: 0 0 0 4px rgba(203, 213, 225, 0.25); }
.ma-today__title { display: block; font-size: 0.72rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
.ma-today__date { display: block; font-size: 0.92rem; font-weight: 600; color: #1f2a44; }
.ma-today__stats { display: flex; flex-wrap: wrap; gap: 1.6rem; }
.ma-today__stat { display: flex; flex-direction: column; gap: 0.15rem; }
.ma-today__stat-label { font-size: 0.7rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; }
.ma-today__stat-value { font-size: 1.1rem; font-weight: 700; color: #1f2a44; font-variant-numeric: tabular-nums; }

/* ── Date range section ── */
.ma-section { background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); margin-bottom: 1.25rem; }
.ma-section__head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.7rem 1rem; border-bottom: 1px solid #eef1f7; background: #fbfcfe; border-radius: 12px 12px 0 0; }
.ma-section__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.ma-section__hint { color: #6b7280; font-size: 0.78rem; font-weight: 600; }
.ma-section__body { padding: 1rem; }

/* ── KPI cards ── */
.ma-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.85rem; margin-bottom: 1.25rem; }
.ma-kpi { display: flex; align-items: center; gap: 0.75rem; background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); padding: 0.85rem 1rem; }
.ma-kpi__icon { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; }
.ma-kpi__icon--present { background: #dcfce7; color: #16834f; }
.ma-kpi__icon--brand { background: #e6f5ef; color: #0e5b46; }
.ma-kpi__meta { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.ma-kpi__label { font-size: 0.72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.ma-kpi__value { font-size: 1.5rem; font-weight: 700; color: #0e5b46; line-height: 1.1; }
.ma-kpi__unit { font-size: 0.8rem; font-weight: 600; color: #16a07a; margin-left: 0.15rem; }

/* ── Date cell ── */
.ma-date { display: flex; flex-direction: column; line-height: 1.25; }
.ma-date__day { font-weight: 600; color: #1f2a44; font-size: 0.86rem; font-variant-numeric: tabular-nums; }
.ma-date__weekday { font-size: 0.72rem; color: #94a3b8; }

.ma-time { font-variant-numeric: tabular-nums; color: #334155; font-weight: 500; }
.ma-punches { font-variant-numeric: tabular-nums; font-weight: 600; color: #475569; }
.ma-muted { color: #cbd5e1; }

/* ── Hours cell + utilization bar ── */
.ma-hours { display: flex; flex-direction: column; gap: 0.3rem; align-items: flex-end; }
.ma-hours__head strong { font-size: 0.86rem; font-weight: 700; color: #1f2a44; font-variant-numeric: tabular-nums; }
.ma-bar { width: 100%; max-width: 110px; height: 5px; border-radius: 999px; background: #eef1f7; overflow: hidden; }
.ma-bar__fill { display: block; height: 100%; border-radius: 999px; transition: width 0.3s ease; }
.ma-bar__fill--full { background: linear-gradient(90deg, #16a07a, #0e5b46); }
.ma-bar__fill--mid { background: linear-gradient(90deg, #fbbf24, #f59e0b); }
.ma-bar__fill--low { background: linear-gradient(90deg, #f87171, #ef4444); }

/* ── Status pill ── */
.ma-pill { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 0.72rem; font-weight: 600; white-space: nowrap; }
.ma-pill__dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.ma-pill--present { background: #dcfce7; color: #166534; }
.ma-pill--absent { background: #f1f5f9; color: #64748b; }
.ma-pill--incomplete { background: #fef3c7; color: #92400e; }
</style>
